import { Dialog } from "@mui/material";
import I18n from "common/i18n";
import { FadeTransition, CloseBtn } from "common/elements";
import { btnDelay, usePlaySound, useScale } from "common/utils";
import {
  BEST_SUBJECT_OPTIONS,
  ACCESSORY_OPTIONS,
  COLOR_PALETTE,
  generateColorVariations,
  BaseColorKey,
} from "../constants";

export type EditingField = "color" | "bestSubjects" | "accessory" | "selfIntroduction" | null;

type ProfileEditModalProps = {
  editingField: EditingField;
  tempValues: string[];
  tempText: string;
  tempBaseColor: BaseColorKey | null;
  tempVariationColor: string | null;
  onTempSelect: (value: string) => void;
  onBaseColorSelect: (baseKey: BaseColorKey) => void;
  onVariationSelect: (hex: string) => void;
  onTempTextChange: (text: string) => void;
  onClose: () => void;
  onConfirm: () => void;
};

const getEditModalTitle = (editingField: EditingField): string => {
  switch (editingField) {
    case "color":
      return I18n.t("MSG_PROFILE_EDIT_COLOR_TITLE");
    case "bestSubjects":
      return I18n.t("MSG_PROFILE_EDIT_BEST_SUBJECTS_TITLE");
    case "accessory":
      return I18n.t("MSG_PROFILE_EDIT_ACCESSORY_TITLE");
    case "selfIntroduction":
      return I18n.t("MSG_PROFILE_AI_OTHER_INFO");
    default:
      return "";
  }
};

const ColorEditContent = ({
  tempBaseColor,
  tempVariationColor,
  onBaseColorSelect,
  onVariationSelect,
}: {
  tempBaseColor: BaseColorKey | null;
  tempVariationColor: string | null;
  onBaseColorSelect: (baseKey: BaseColorKey) => void;
  onVariationSelect: (hex: string) => void;
}) => {
  const play = usePlaySound();
  const variations = tempBaseColor ? generateColorVariations(tempBaseColor) : [];

  const handleBaseColorClick = (baseKey: BaseColorKey) => {
    play();
    onBaseColorSelect(baseKey);
  };

  const handleVariationClick = (hex: string) => {
    play();
    onVariationSelect(hex);
  };

  return (
    <div className="flex-col-el items-center gap-6">
      <div className="flex-row-el flex-center gap-5">
        {COLOR_PALETTE.map((option) => {
          const isActive = tempBaseColor === option.key;
          const isWhite = option.key === "white";
          return (
            <div
              key={option.key}
              onClick={() => handleBaseColorClick(option.key)}
              className="flex-col-el flex-center gap-2 cursor-pointer select-none"
            >
              <div
                className={`
                  flex-col-el flex-center
                  w-[80px] h-[80px] rounded-full active:scale-95
                  ${isActive ? "ring-4 ring-green-100" : ""}
                  ${isWhite ? "border-2 border-gray-40" : ""}
                `}
                style={{ backgroundColor: option.hex }}
              >
                {isActive && (
                  <span className="material-symbols-outlined text-[32px] text-white drop-shadow-md">
                    check
                  </span>
                )}
              </div>
              <span
                className={`text-body text-[16px] ${isActive ? "text-green-100" : "text-textcolor-black/70"}`}
              >
                {I18n.t(option.labelKey)}
              </span>
            </div>
          );
        })}
      </div>

      <div className="flex-col-el items-center gap-4">
        <p className="text-body text-textcolor-black text-[18px]">
          {I18n.t("MSG_COLOR_SELECT_SHADE")}
        </p>
        <div className="flex-row-el gap-4">
          {tempBaseColor
            ? variations.map((hex, idx) => {
                const isSelected = tempVariationColor === hex;
                return (
                  <div
                    key={idx}
                    onClick={() => handleVariationClick(hex)}
                    className={`
                      flex-col-el flex-center w-[60px] h-[60px] rounded-full
                      cursor-pointer select-none active:scale-95
                      ${isSelected ? "ring-4 ring-green-100" : ""}
                    `}
                    style={{ backgroundColor: hex }}
                  >
                    {isSelected && (
                      <span className="material-symbols-outlined text-[24px] text-white drop-shadow-md">
                        check
                      </span>
                    )}
                  </div>
                );
              })
            : Array.from({ length: 5 }).map((_, idx) => (
                <div
                  key={idx}
                  className="flex-col-el flex-center w-[60px] h-[60px] rounded-full border-2 border-dashed border-gray-40"
                />
              ))}
        </div>
      </div>
    </div>
  );
};

const SubjectsEditContent = ({
  tempValues,
  onTempSelect,
}: {
  tempValues: string[];
  onTempSelect: (value: string) => void;
}) => {
  const play = usePlaySound();

  const handleSelect = (value: string) => {
    play();
    onTempSelect(value);
  };

  return (
    <div className="grid grid-cols-4 gap-3 justify-items-center">
      {BEST_SUBJECT_OPTIONS.map((option) => {
        const isActive = tempValues.includes(option.key);
        return (
          <div
            key={option.key}
            onClick={() => handleSelect(option.key)}
            className={`
              flex-col-el flex-center relative
              w-[120px] h-[120px]
              rounded-[8px]
              cursor-pointer select-none
              active:scale-95
              ${isActive ? "bg-green-alt border-2 border-green-alt" : "bg-surface border-2 border-green-alt"}
            `}
          >
            {isActive && (
              <span className="absolute top-1 right-1 material-symbols-outlined text-[16px] text-green-100">
                check_circle
              </span>
            )}
            {option.icon && (
              <span
                className={`material-symbols-outlined !text-[32px] mb-1 ${isActive ? "text-green-100" : "text-green-80"}`}
              >
                {option.icon}
              </span>
            )}
            <span
              className={`text-body text-[20px] text-center ${isActive ? "text-textcolor-black" : "text-textcolor-black/70"}`}
            >
              {I18n.t(option.labelKey)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const AccessoryEditContent = ({
  tempValues,
  onTempSelect,
}: {
  tempValues: string[];
  onTempSelect: (value: string) => void;
}) => {
  const play = usePlaySound();

  const handleSelect = (value: string) => {
    play();
    onTempSelect(value);
  };

  return (
    <div className="grid grid-cols-3 gap-4 justify-items-center">
      {ACCESSORY_OPTIONS.map((option) => {
        const isActive = tempValues.includes(option.key);
        return (
          <div
            key={option.key}
            onClick={() => handleSelect(option.key)}
            className={`
              flex-col-el flex-center relative
              w-[140px] h-[140px]
              rounded-[8px]
              cursor-pointer select-none
              active:scale-95
              ${isActive ? "bg-green-alt border-2 border-green-alt" : "bg-surface border-2 border-green-alt"}
            `}
          >
            {isActive && (
              <span className="absolute top-1 right-1 material-symbols-outlined text-[16px] text-green-100">
                check_circle
              </span>
            )}
            {option.icon && (
              <span
                className={`material-symbols-outlined !text-[36px] mb-1 ${isActive ? "text-green-100" : "text-green-80"}`}
              >
                {option.icon}
              </span>
            )}
            <span
              className={`text-body text-[20px] text-center ${isActive ? "text-textcolor-black" : "text-textcolor-black/70"}`}
            >
              {I18n.t(option.labelKey)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const SelfIntroductionEditContent = ({
  tempText,
  onTempTextChange,
}: {
  tempText: string;
  onTempTextChange: (text: string) => void;
}) => (
  <div className="flex-col-el items-center w-full">
    <div className="flex-col-el w-full max-w-[520px]">
      <textarea
        className="input-modal text-body text-textcolor-black text-[18px] resize-none"
        rows={5}
        placeholder={I18n.t("MSG_PROFILE_AI_OTHER_INFO_PLACEHOLDER")}
        value={tempText}
        onChange={(e) => onTempTextChange(e.target.value)}
        maxLength={200}
      />
      <p className="text-body text-right text-[14px] text-gray2-80 mt-2">{tempText.length}/200</p>
    </div>
  </div>
);

export const ProfileEditModal = ({
  editingField,
  tempValues,
  tempText,
  tempBaseColor,
  tempVariationColor,
  onTempSelect,
  onBaseColorSelect,
  onVariationSelect,
  onTempTextChange,
  onClose,
  onConfirm,
}: ProfileEditModalProps) => {
  const play = usePlaySound();
  const { scale } = useScale();

  const handleClose = () => {
    play();
    btnDelay(() => onClose());
  };

  const handleConfirm = () => {
    play();
    btnDelay(() => onConfirm());
  };

  const getModalHeight = () => {
    switch (editingField) {
      case "bestSubjects":
        return 780;
      case "accessory":
        return 580;
      case "color":
        return 440;
      default:
        return 450;
    }
  };
  const modalHeight = getModalHeight();

  const renderContent = () => {
    switch (editingField) {
      case "color":
        return (
          <ColorEditContent
            tempBaseColor={tempBaseColor}
            tempVariationColor={tempVariationColor}
            onBaseColorSelect={onBaseColorSelect}
            onVariationSelect={onVariationSelect}
          />
        );
      case "bestSubjects":
        return <SubjectsEditContent tempValues={tempValues} onTempSelect={onTempSelect} />;
      case "accessory":
        return <AccessoryEditContent tempValues={tempValues} onTempSelect={onTempSelect} />;
      case "selfIntroduction":
        return (
          <SelfIntroductionEditContent tempText={tempText} onTempTextChange={onTempTextChange} />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={editingField !== null}
      fullScreen
      onClose={handleClose}
      maxWidth={false}
      sx={{ zIndex: 999999 }}
      TransitionComponent={FadeTransition}
      componentsProps={{
        backdrop: { style: { backgroundColor: "rgba(0, 0, 0, 0.5)" } },
      }}
      PaperProps={{
        style: { margin: 0, overflow: "visible", backgroundColor: "transparent" },
        elevation: 0,
      }}
    >
      <div className="flex-col-el flex-center w-full h-full">
        <div
          className="flex-col-el flex-center relative"
          style={{
            width: 660 * scale,
            height: modalHeight * scale,
          }}
        >
          <div
            className="flex-col-el shrink-0 border-solid border-white border-[8px] rounded-[8px] bg-beige-60 relative"
            style={{
              width: 660,
              height: modalHeight,
              transform: `scale(${scale})`,
              filter:
                "drop-shadow(0px 6px 6px rgba(0, 0, 0, 0.3)) drop-shadow(0px 6px 6px rgba(0, 0, 0, 0.3))",
            }}
          >
            <CloseBtn close={handleClose} />

            <div className="flex-col-el flex-center py-4">
              <p className="text-body text-textcolor-black text-[24px]">
                {getEditModalTitle(editingField)}
              </p>
              {(editingField === "bestSubjects" || editingField === "accessory") && (
                <p className="text-[14px] text-textcolor-black/60 mt-1">
                  ({I18n.t("MSG_PROFILE_MULTI_SELECT")})
                </p>
              )}
            </div>

            <div className="flex-col-el flex-center flex-1 px-4">{renderContent()}</div>

            <div className="flex-col-el flex-center py-4 alpha">
              <button
                className="btn btn-primary"
                onClick={handleConfirm}
                disabled={
                  (editingField === "color" && !tempVariationColor) ||
                  (editingField === "bestSubjects" && tempValues.length === 0) ||
                  (editingField === "accessory" && tempValues.length === 0)
                }
              >
                <div className="flex-row-center gap-1 mb-[3px]">
                  <span className="material-symbols-outlined !text-4xl text-white">check</span>
                  <p className="text-body text-white text-font-size-body1">
                    {I18n.t("MSG_CONFIRM_BTN")}
                  </p>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
